import api from "../../Utils/api";
import React, { useRef, useEffect, useState } from "react";
import { toast } from "react-toastify";
import PasswordResetDialog from "./ResetPassword";
import Spinner from "../Spinner";

// Helper: default user data
const defaultUser = {
  id: null,
  firstName: "",
  lastName: "",
  preferredName: "",
  pronouns: "",
  birthday: "",
  email: "",
  phone: "",
  address: "",
  postCode: "",
  city: "",
  state: "",
  country: "",
  emergencyName: "",
  emergencyPhone: "",
  kioskPin: "1234",
  profileImage: null, // File or data URL
};

export default function PersonalDetailsDialog({
  id,
  open,
  onClose,
  initialUser = null,
  onSave,
  onDelete,
}) {
  const [user, setUser] = useState(initialUser || defaultUser);
  const [editing, setEditing] = useState(!initialUser); // If no user, it's create mode
  const [isLoading, setIsLoading] = useState(false);

  // Section refs for scrolling
  const personalRef = useRef(null);
  const contactRef = useRef(null);
  const loginRef = useRef(null);
  const scrollAreaRef = useRef(null);

  const [activeTab, setActiveTab] = useState("personal");

  // Prevent background scroll
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  useEffect(() => {
    if (id !== "") getUserProfile();
  }, [id]);

  const getUserProfile = async () => {
    setIsLoading(true);
    const res = await api.get(`/user/${id}`);
    setIsLoading(false);
    if (res.data.success) {
      setUser(res.data.user);
    } else {
      console.error("Failed to fetch user profile");
    }
  };

  // Scroll to section
  const scrollToSection = (ref, tab) => {
    setActiveTab(tab);
    if (ref.current && scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current;
      const sectionTop = ref.current.offsetTop - 280;
      scrollArea.scrollTo({ top: sectionTop, behavior: "smooth" });
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((u) => ({ ...u, [name]: value }));
  };

  // Handle save
  const handleSave = async () => {
    let requestBody = {
      _id: user._id,
      updatedData: user,
    };

    setIsLoading(true);
    const res = await api.post("/user/update", requestBody);
    setIsLoading(false);
    if (res.data.success) {
      toast.success("Profile updated successfully!");
      setEditing(false);
      if (onSave) onSave(user);
    } else {
      toast.error("Failed to update profile: " + res.data.message);
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm("Delete this profile?") && onDelete) {
      onDelete(user.id);
      onClose();
    }
  };

  // Handle create new
  const handleCreateNew = () => {
    // setUser(defaultUser);
    // setEditing(true);
    // setImagePreview(null);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
      {isLoading && <Spinner />}
      <div className="bg-white w-full max-w-5xl max-h-[90vh] flex flex-col rounded-lg">
        {/* Header */}
        <div className="p-6 flex justify-between items-start">
          <div>
            <div>
              <h1 className="text-3xl font-bold mb-1">{"Personal"}</h1>
              <div className="text-lg text-gray-600">{user.preferredName}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-6 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Back
            </button>
            <button
              onClick={editing ? handleSave : () => setEditing(true)}
              className="px-6 py-2 rounded bg-gray-600 text-white hover:bg-gray-900"
            >
              {editing ? "Save" : "Edit"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "personal"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(personalRef, "personal")}
              type="button"
            >
              Personal details
            </button>
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "contact"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(contactRef, "contact")}
              type="button"
            >
              Contact
            </button>
            <button
              className={`py-2 font-medium border-b-2 transition-colors duration-200 ${
                activeTab === "login"
                  ? "border-gray-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:text-gray-600"
              }`}
              onClick={() => scrollToSection(loginRef, "login")}
              type="button"
            >
              Login information
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollAreaRef}
          className="overflow-y-auto  flex-1 p-6 space-y-16"
          style={{ scrollBehavior: "smooth" }}
        >
          {/* Personal Details */}
          <section ref={personalRef}>
            <h2 className="text-2xl font-semibold mb-8">Personal details</h2>
            {/* <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Profile Picture</p>
              </div>
              <div className="col-span-2">
                <div className="flex flex-wrap items-center mr-10">
                  <div className="w-16 h-16 rounded-full mr-4 bg-gray-100 flex items-center justify-center text-2xl font-semibold text-gray-700 overflow-hidden">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile"
                        className="object-cover w-full h-full rounded-full"
                      />
                    ) : (
                      (user.firstName?.[0] || "") + (user.lastName?.[0] || "")
                    )}
                  </div>
                  <div>
                    {editing && (
                      <>
                        <label className="text-sm text-gray-800 bg-gray-100 px-3 py-1 rounded mb-1 cursor-pointer">
                          Upload photo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageChange}
                          />
                        </label>
                        <div className="text-xs text-gray-500">
                          Accepted: JPG, JPEG, PNG. Max size 10MB
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div> */}
            {/* First Name */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">First Name</p>
              </div>
              <div className="col-span-2">
                <input
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="firstName"
                  value={user.firstName}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
            {/* Last Name */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Last Name</p>
              </div>
              <div className="col-span-2">
                <input
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="lastName"
                  value={user.lastName}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
            {/* Full Name */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Preferred full name</p>
                <p className="text-xs text-gray-500 mt-1">
                  Their display name in Deputy.
                </p>
              </div>
              <div className="col-span-2">
                <input
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="preferredName"
                  value={user.preferredName}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
            {/* Pronouns */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Pronouns</p>
                <p className="text-xs text-gray-500 mt-1">
                  This will appear next to a team member's name.
                </p>
              </div>
              <div className="col-span-2">
                <select
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="pronouns"
                  value={user.pronouns}
                  onChange={handleChange}
                  disabled={!editing}
                >
                  <option value="">Not Specified</option>
                  <option value="They / Them">They / Them</option>
                  <option value="She / Her">She / Her</option>
                  <option value="He / Him">He / Him</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>
            </div>
            {/* DOB */}
            <div className="grid grid-cols-5 mb-8">
              <div>
                <p className="font-semibold text-sm">Date of birth</p>
              </div>
              <div className="col-span-2">
                <input
                  type="date"
                  className="w-full rounded border-gray-300 bg-slate-100 p-2"
                  name="birthday"
                  value={user.birthday}
                  onChange={handleChange}
                  disabled={!editing}
                />
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section ref={contactRef}>
            <h2 className="text-2xl font-semibold mb-8">Contact</h2>
            <div className="space-y-5">
              {/* Email */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Email</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="email"
                    value={user.email}
                    disabled
                    onChange={handleChange}
                  />
                </div>
              </div>
              {/* Mobile */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Mobile</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="phone"
                    value={user.phone}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Address */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Address</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="address"
                    value={user.address}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Postcode */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Postcode</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="postCode"
                    value={user.postCode}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* City */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">City</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="city"
                    value={user.city}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* State */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">State</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="state"
                    value={user.state}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Country */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">Country</p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="country"
                    value={user.country}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Emergency Contact Name */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">
                    Emergency contact name
                  </p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="emergencyName"
                    value={user.emergencyName}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
              {/* Emergency Phone */}
              <div className="grid grid-cols-5 items-center mb-2">
                <div>
                  <p className="font-semibold text-sm">
                    Emergency phone number
                  </p>
                </div>
                <div className="col-span-2">
                  <input
                    className="w-full rounded border-gray-300 bg-slate-100 p-2"
                    name="emergencyPhone"
                    value={user.emergencyPhone}
                    onChange={handleChange}
                    disabled={!editing}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Login Information Section */}
          <section ref={loginRef}>
            <h2 className="text-2xl font-semibold mb-8">Login information</h2>
            <div className="grid grid-cols-5 items-center mb-8">
              <div>
                <p className="font-semibold text-sm">Reset Password</p>
              </div>
              <div className="col-span-2">
                <PasswordResetDialog id={id} />
              </div>
            </div>
          </section>
        </div>
        {/* Create New Button (for demo) */}
        {!editing && !user.id && (
          <button
            className="m-6 px-6 py-2 rounded bg-blue-600 text-white hover:bg-blue-900"
            onClick={handleCreateNew}
          >
            Create New Profile
          </button>
        )}
      </div>
    </div>
  );
}
